import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { useMessageTemplates } from '@/hooks/useMessageTemplates';
import type { MessageTemplate } from '@/data/messageTemplates.service';
import { renderTemplate, TEMPLATE_TOKENS, insertAtCursor } from '@/lib/templateUtils';
import { buildWaMeUrl } from '@/lib/whatsapp';
import { getTelHref, openTel, normalizePhoneForTel } from '@/lib/phone';
import { toast } from 'sonner';
import { Phone, Copy } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export type LeadLike = {
  id?: string;
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  city?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
};

export type LogOutboundPayload = {
  leadId: string;
  phone: string;
  message: string;
  templateId?: string;
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  lead: LeadLike | null;
  onLogOutbound?: (payload: LogOutboundPayload) => void;
  onManageTemplates?: () => void;
};

export function TemplatePickerDialog({
  open,
  onOpenChange,
  lead,
  onLogOutbound,
  onManageTemplates,
}: Props) {
  const { data: templates = [], isLoading } = useMessageTemplates(undefined, {
    scopeType: 'keep_warm',
  });
  const [query, setQuery] = React.useState('');
  const [selected, setSelected] = React.useState<MessageTemplate | null>(null);
  const [draft, setDraft] = React.useState('');

  React.useEffect(() => {
    if (!open) {
      setQuery('');
      setSelected(null);
      setDraft('');
      return;
    }
    if (templates.length && !selected) {
      setSelected(templates[0]);
      setDraft(templates[0].body ?? '');
    }
  }, [open, templates]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (selected) setDraft(selected.body ?? '');
  }, [selected]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) =>
        (t.name ?? '').toLowerCase().includes(q) ||
        (t.body ?? '').toLowerCase().includes(q)
    );
  }, [templates, query]);

  const vars = React.useMemo(
    () => ({
      first_name: lead?.first_name ?? '',
      last_name: lead?.last_name ?? '',
      company: lead?.company ?? '',
      city: lead?.city ?? '',
    }),
    [lead]
  );

  const compiled = React.useMemo(() => renderTemplate(draft, vars), [draft, vars]);

  const phoneRaw = lead?.whatsapp || lead?.phone || '';
  const phoneNormalized = normalizePhoneForTel(phoneRaw);
  const hasPhone = phoneRaw.trim().length > 0;
  const telHref = getTelHref(phoneRaw);

  const onCopy = async () => {
    await navigator.clipboard.writeText(compiled);
    toast.success('Message copied');
  };

  const onCopyPhone = async () => {
    const text = phoneNormalized || phoneRaw.trim();
    if (!text) return;
    await navigator.clipboard.writeText(text);
    toast.success('Phone number copied');
  };

  const onOpenWhatsApp = () => {
    if (!lead?.id || !phoneRaw) return;
    const url = buildWaMeUrl(phoneRaw, compiled);
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) {
      toast.error('Popup blocked: allow popups');
      return;
    }
    try {
      onLogOutbound?.({
        leadId: lead.id,
        phone: phoneRaw,
        message: compiled,
        templateId: selected?.id,
      });
    } catch {
      // non-blocking
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-[920px] p-0 overflow-hidden gap-0',
          'bg-background/80 border-border/60 backdrop-blur-xl shadow-2xl'
        )}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <DialogHeader className="space-y-1.5">
            <DialogTitle className="text-xl font-semibold text-foreground">
              Send WhatsApp
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Choose a template, preview, then open WhatsApp with the message prefilled.
            </DialogDescription>
          </DialogHeader>
        </div>
        <Separator className="bg-border/60" />

        <div className="grid grid-cols-12 gap-0">
          {/* LEFT: templates */}
          <div className="col-span-5 flex flex-col bg-card/40 border-r border-border/60">
            <div className="p-4">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search templates…"
                className="h-10 rounded-xl bg-muted/25 border-border/60 text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:ring-offset-0 transition-colors"
              />
            </div>
            <ScrollArea className="h-[420px] flex-1">
              <div className="px-3 pb-4">
                {isLoading ? (
                  <div className="py-4 text-sm text-muted-foreground">
                    Loading templates…
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="py-4 space-y-3">
                    <p className="text-sm text-muted-foreground">
                      No templates found.
                    </p>
                    <Button
                      variant="secondary"
                      onClick={onManageTemplates}
                      className="w-full rounded-xl bg-muted/25 border-border/60 hover:bg-muted/40 text-foreground"
                    >
                      Create / Manage templates
                    </Button>
                  </div>
                ) : (
                  filtered.map((t) => {
                    const active = selected?.id === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setSelected(t)}
                        className={cn(
                          'w-full text-left rounded-xl px-3 py-3 mb-2 transition-all duration-200 ease-out',
                          'border hover:bg-muted/20 hover:border-border/60 active:bg-muted/30',
                          active
                            ? 'border-border/60 bg-muted/30 ring-1 ring-primary/25 text-foreground'
                            : 'border-transparent bg-transparent text-foreground'
                        )}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-medium truncate text-foreground">{t.name}</span>
                          <span className="text-[11px] text-muted-foreground shrink-0">
                            {t.scope_type === 'global' ? 'GLOBAL' : 'WORKSPACE'}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                          {t.body}
                        </p>
                      </button>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>

          {/* RIGHT: editor + preview */}
          <div className="col-span-7 flex flex-col">
            <div className="p-4 flex items-center justify-between gap-3">
              <span className="text-sm text-muted-foreground">Tokens</span>
              <div className="flex flex-wrap gap-2 justify-end">
                {TEMPLATE_TOKENS.map((tok) => (
                  <Button
                    key={tok}
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-7 px-2.5 rounded-lg text-xs bg-muted/20 border border-border/60 text-foreground hover:bg-muted/40 hover:border-border/80 transition-colors"
                    onClick={() => {
                      const ta = document.getElementById(
                        'template-draft'
                      ) as HTMLTextAreaElement | null;
                      if (!ta) return;
                      const next = insertAtCursor(ta, tok);
                      setDraft(next);
                    }}
                  >
                    {tok}
                  </Button>
                ))}
              </div>
            </div>
            <div className="px-4 pb-4 flex-1 min-w-0">
              <Textarea
                id="template-draft"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Write your message…"
                className="min-h-[180px] resize-none rounded-2xl bg-muted/15 border-border/60 text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:ring-offset-0 transition-colors"
              />
              <div className="mt-4 rounded-xl border border-border/60 bg-muted/10 p-4">
                <p className="text-xs text-muted-foreground mb-2">Preview</p>
                <div className="text-sm text-foreground whitespace-pre-wrap">
                  {compiled || '—'}
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onCopy}
                  className="rounded-xl bg-muted/25 border-border/60 text-foreground hover:bg-muted/40 transition-colors"
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" />
                  Copy
                </Button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={onCopyPhone}
                          disabled={!hasPhone}
                          className="rounded-xl bg-muted/25 border-border/60 text-foreground hover:bg-muted/40 transition-colors disabled:opacity-50"
                        >
                          <Copy className="w-3.5 h-3.5 mr-1.5" />
                          Copy phone
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {hasPhone ? 'Copy phone number' : 'No phone available'}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => openTel(phoneRaw)}
                          disabled={!telHref}
                          className="rounded-xl bg-muted/25 border-border/60 text-foreground hover:bg-muted/40 transition-colors disabled:opacity-50"
                        >
                          <Phone className="w-3.5 h-3.5 mr-1.5" />
                          Send to phone
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {telHref ? 'Open phone dialer' : 'No phone available'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Button
                  onClick={onOpenWhatsApp}
                  disabled={!hasPhone || !compiled}
                  className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm transition-colors"
                >
                  Open WhatsApp
                </Button>
              </div>
              {!hasPhone ? (
                <p className="mt-3 text-xs text-muted-foreground">
                  No phone number for this lead. Add a number to use Send to phone or WhatsApp.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
